import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PumpAnalysis } from "@shared/schema";
import { Chart, registerables, ChartDataset } from "chart.js";
import { formatCurrency, formatNumber } from "@/lib/utils";
import zoomPlugin from "chartjs-plugin-zoom";

// Register all Chart.js components and plugins
Chart.register(...registerables, zoomPlugin);

interface ResultsChartsProps {
  type: string;
  recommendation?: any;
  recommendations?: any[];
  analysis: PumpAnalysis;
  analyticsData?: any;
}

export function ResultsCharts({
  type,
  recommendation,
  recommendations,
  analysis,
  analyticsData
}: ResultsChartsProps) {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  
  // Define translated strings once to be passed to chart functions
  const translations = {
    t: t,
    existingPump: t('charts.existingPump'),
    recommendedPump: t('charts.recommendedPump'),
    powerKW: t('charts.powerKW'),
    loadPercent: t('charts.loadPercent'),
    currentSystem: t('charts.currentSystem'),
    annualSavings: t('charts.annualSavings'),
    energyKWh: t('charts.energyKWh'),
    costDollars: t('charts.costDollars'),
    years: t('charts.years'),
    savings: t('charts.savings'),
    payback: t('charts.payback'),
    roi: t('charts.roi'),
    efficiency: t('charts.efficiency'),
    flow: t('charts.flow'),
    head: t('charts.head'),
    comparison: t('charts.comparison'),
    before: t('charts.before'),
    after: t('charts.after'),
    reduction: t('charts.reduction'),
    // Interactive chart elements
    zoomIn: t('charts.interactive.zoomIn'),
    zoomOut: t('charts.interactive.zoomOut'),
    reset: t('charts.interactive.reset'),
    download: t('charts.interactive.download'),
    expandChart: t('charts.interactive.expandChart'),
    loadProfile: t('charts.interactive.loadProfile'),
    tornadoDiagram: t('charts.interactive.tornadoDiagram'),
    sensitivityAnalysis: t('charts.interactive.sensitivityAnalysis'),
    monteCarloSimulation: t('charts.interactive.monteCarloSimulation'),
    dragToZoom: t('charts.interactive.dragToZoom'),
    scrollToZoom: t('charts.interactive.scrollToZoom')
  };
  
  useEffect(() => {
    if (!chartRef.current) return;
    
    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    
    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;
    
    let chart;
    
    switch (type) {
      case 'powerCurve':
        chart = createPowerCurveChart(ctx, recommendation, analysis, translations);
        break;
      case 'energySavings':
        chart = createEnergySavingsChart(ctx, recommendation, analysis, translations);
        break;
      case 'costOverTime':
        chart = createCostOverTimeChart(ctx, recommendation, analysis, translations);
        break;
      case 'efficiencyComparison':
        chart = createEfficiencyComparisonChart(ctx, recommendation, analysis, translations);
        break;
      case 'efficiencyMap':
        chart = createEfficiencyMapChart(ctx, recommendation, analysis, translations);
        break;
      case 'npvComparison':
        chart = createNpvComparisonChart(ctx, recommendations || [], translations);
        break;
      case 'savingsComparison':
        chart = createSavingsComparisonChart(ctx, recommendations || [], translations);
        break;
      case 'engineeringRadar':
        chart = createEngineeringRadarChart(ctx, recommendations || [], translations);
        break;
      case 'emissionsReduction':
        chart = createEmissionsReductionChart(ctx, recommendations || [], translations);
        break;
      case 'sensitivityCurves':
        chart = createSensitivityCurvesChart(ctx, analyticsData, recommendations, translations);
        break;
      case 'waterfall':
        chart = createWaterfallChart(ctx, recommendation, analysis, translations);
        break;
      case 'tornadoDiagram':
        chart = createTornadoDiagramChart(ctx, analyticsData, recommendation, translations);
        break;
      case 'loadProfile':
        chart = createLoadProfileChart(ctx, analysis, translations);
        break;
      case 'lifecycleCost':
        chart = createLifecycleCostChart(ctx, recommendation, analysis, translations);
        break;
      default:
        console.warn(`Chart type '${type}' not recognized`);
        return;
    }
    
    chartInstance.current = chart;
    
    // Cleanup on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [type, recommendation, recommendations, analysis, analyticsData, t]);
  
  return <canvas ref={chartRef} />;
}

function createPowerCurveChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  // If powerCurve data is missing, create some plausible data
  const powerCurve = recommendation.powerCurve || [
    { loadPercentage: 0, power: 0.1 },
    { loadPercentage: 10, power: 0.18 },
    { loadPercentage: 20, power: 0.26 },
    { loadPercentage: 30, power: 0.35 },
    { loadPercentage: 40, power: 0.47 },
    { loadPercentage: 50, power: 0.60 },
    { loadPercentage: 60, power: 0.76 },
    { loadPercentage: 70, power: 0.94 },
    { loadPercentage: 80, power: 1.16 },
    { loadPercentage: 90, power: 1.42 },
    { loadPercentage: 100, power: 1.71 }
  ];
  
  // Original pump power curve (estimated based on savings if not provided)
  const originalCurve = recommendation.originalPowerCurve || powerCurve.map((point: any) => ({
    loadPercentage: point.loadPercentage,
    power: point.power * 1.6 // Assume original is ~60% less efficient
  }));
  
  return new Chart(ctx, {
    type: 'line',
    data: {
      labels: powerCurve.map((point: any) => `${point.loadPercentage}%`),
      datasets: [
        {
          label: translations.existingPump,
          data: originalCurve.map((point: any) => point.power),
          borderColor: '#f43f5e',
          backgroundColor: 'rgba(244, 63, 94, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1
        },
        {
          label: recommendation.model,
          data: powerCurve.map((point: any) => point.power),
          borderColor: '#0891b2',
          backgroundColor: 'rgba(8, 145, 178, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          title: {
            display: true,
            text: translations.powerKW
          },
          beginAtZero: true
        },
        x: {
          title: {
            display: true,
            text: translations.loadPercent
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.raw} kW`;
            }
          }
        }
      }
    }
  });
}

function createEnergySavingsChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  const currentEnergy = recommendation.annualEnergySavings * 2; // Assuming original is roughly double
  const newEnergy = recommendation.annualEnergySavings;
  const savings = currentEnergy - newEnergy;
  
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['Current System', recommendation.model, 'Annual Savings'],
      datasets: [
        {
          label: 'Energy (kWh/year)',
          data: [currentEnergy, newEnergy, savings],
          backgroundColor: [
            'rgba(244, 63, 94, 0.7)',
            'rgba(8, 145, 178, 0.7)',
            'rgba(16, 185, 129, 0.7)'
          ],
          borderColor: [
            'rgb(244, 63, 94)',
            'rgb(8, 145, 178)',
            'rgb(16, 185, 129)'
          ],
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Energy (kWh/year)'
          },
          beginAtZero: true
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${formatNumber(context.raw as number)} kWh/year`;
            }
          }
        }
      }
    }
  });
}

function createCostOverTimeChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  const years = Array.from({ length: 11 }, (_, i) => i);
  
  // Initial costs
  const initialCost = recommendation.estimatedPrice + 
    (recommendation.installationCost || recommendation.estimatedPrice * 0.25) + 500;
  
  // Annual operating costs
  const existingAnnualCost = (recommendation.annualEnergySavings * 2 * analysis.utilityRate) + 
    (analysis.maintenanceCost || 2000);
  const newAnnualCost = (recommendation.annualEnergySavings * analysis.utilityRate) + 
    ((analysis.maintenanceCost || 2000) * 0.5);
  
  // Cumulative costs
  const existingCumulative = years.map(year => year * existingAnnualCost);
  const newCumulative = years.map(year => initialCost + (year * newAnnualCost));
  
  // Break-even point
  const breakEvenYear = Math.ceil(initialCost / (existingAnnualCost - newAnnualCost));
  const breakEvenData = years.map(year => year === breakEvenYear ? 
    existingCumulative[breakEvenYear] : null);
  
  return new Chart(ctx, {
    type: 'line',
    data: {
      labels: years.map(year => `Year ${year}`),
      datasets: [
        {
          label: 'Existing System Cumulative Cost',
          data: existingCumulative,
          borderColor: '#f43f5e',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderWidth: 2
        },
        {
          label: `${recommendation.model} Cumulative Cost`,
          data: newCumulative,
          borderColor: '#0891b2',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderWidth: 2
        },
        {
          label: 'Break-Even Point',
          data: breakEvenData,
          pointRadius: 8,
          pointBackgroundColor: '#10b981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointStyle: 'star',
          showLine: false
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Cumulative Cost ($)'
          },
          beginAtZero: true
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              if (context.dataset.label === 'Break-Even Point') {
                return `Break-Even: Year ${breakEvenYear}`;
              }
              return `${context.dataset.label}: ${formatCurrency(context.raw as number)}`;
            }
          }
        }
      }
    }
  });
}

function createEfficiencyComparisonChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  // Existing efficiencies (if not provided, use estimated values)
  const existingMotorEfficiency = analysis.efficiency || 75;
  const existingPumpEfficiency = existingMotorEfficiency * 0.85;
  const existingSystemEfficiency = existingMotorEfficiency * 0.8;
  
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['Motor', 'Pump', 'System'],
      datasets: [
        {
          label: 'Existing',
          data: [existingMotorEfficiency, existingPumpEfficiency, existingSystemEfficiency],
          backgroundColor: 'rgba(148, 163, 184, 0.7)',
          borderColor: 'rgba(148, 163, 184, 1)',
          borderWidth: 1
        },
        {
          label: 'Replacement',
          data: [
            recommendation.motorEfficiency, 
            recommendation.pumpEfficiency, 
            recommendation.systemEfficiency
          ],
          backgroundColor: 'rgba(14, 165, 233, 0.7)',
          borderColor: 'rgba(14, 165, 233, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Efficiency (%)'
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${formatNumber(context.raw as number, 1)}%`;
            }
          }
        }
      }
    }
  });
}

function createNpvComparisonChart(ctx: CanvasRenderingContext2D, recommendations: any[], translations: any) {
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: recommendations.map(rec => `${rec.manufacturer} ${rec.model}`),
      datasets: [
        {
          label: 'Net Present Value ($)',
          data: recommendations.map(rec => rec.npv),
          backgroundColor: recommendations.map((_, i) => {
            const colors = [
              'rgba(8, 145, 178, 0.7)',
              'rgba(249, 115, 22, 0.7)',
              'rgba(139, 92, 246, 0.7)',
              'rgba(16, 185, 129, 0.7)',
              'rgba(244, 63, 94, 0.7)'
            ];
            return colors[i % colors.length];
          }),
          borderColor: recommendations.map((_, i) => {
            const colors = [
              'rgb(8, 145, 178)',
              'rgb(249, 115, 22)',
              'rgb(139, 92, 246)',
              'rgb(16, 185, 129)',
              'rgb(244, 63, 94)'
            ];
            return colors[i % colors.length];
          }),
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Net Present Value ($)'
          },
          beginAtZero: true
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `NPV: ${formatCurrency(context.raw as number)}`;
            }
          }
        }
      }
    }
  });
}

function createSavingsComparisonChart(ctx: CanvasRenderingContext2D, recommendations: any[], translations: any) {
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: recommendations.map(rec => `${rec.manufacturer} ${rec.model}`),
      datasets: [
        {
          label: 'Annual Savings ($)',
          data: recommendations.map(rec => rec.annualCostSavings + (rec.maintenanceSavings || 250)),
          backgroundColor: recommendations.map((_, i) => {
            const colors = [
              'rgba(8, 145, 178, 0.7)',
              'rgba(249, 115, 22, 0.7)',
              'rgba(139, 92, 246, 0.7)',
              'rgba(16, 185, 129, 0.7)',
              'rgba(244, 63, 94, 0.7)'
            ];
            return colors[i % colors.length];
          }),
          borderColor: recommendations.map((_, i) => {
            const colors = [
              'rgb(8, 145, 178)',
              'rgb(249, 115, 22)',
              'rgb(139, 92, 246)',
              'rgb(16, 185, 129)',
              'rgb(244, 63, 94)'
            ];
            return colors[i % colors.length];
          }),
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Annual Savings ($)'
          },
          beginAtZero: true
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `Annual Savings: ${formatCurrency(context.raw as number)}`;
            }
          }
        }
      }
    }
  });
}

function createEngineeringRadarChart(ctx: CanvasRenderingContext2D, recommendations: any[], translations: any) {
  return new Chart(ctx, {
    type: 'radar',
    data: {
      labels: [
        'Compatibility Score',
        'Space Fit',
        'Material Suitability',
        'Control Complexity'
      ],
      datasets: recommendations.map((rec, i) => {
        const colors = [
          'rgba(8, 145, 178, 0.7)',
          'rgba(249, 115, 22, 0.7)',
          'rgba(139, 92, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(244, 63, 94, 0.7)'
        ];
        return {
          label: `${rec.manufacturer} ${rec.model}`,
          data: [
            rec.compatibilityScore,
            rec.spaceFit || rec.compatibilityScore * 1.05,
            85, // Default material suitability if not provided
            rec.controlComplexity || 90
          ],
          backgroundColor: colors[i % colors.length],
          borderColor: colors[i % colors.length].replace('0.7', '1'),
          borderWidth: 1
        };
      })
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          angleLines: {
            display: true
          },
          suggestedMin: 0,
          suggestedMax: 100
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${formatNumber(context.raw as number, 0)}/100`;
            }
          }
        }
      }
    }
  });
}

function createEmissionsReductionChart(ctx: CanvasRenderingContext2D, recommendations: any[], translations: any) {
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: recommendations.map(rec => `${rec.manufacturer} ${rec.model}`),
      datasets: [
        {
          label: 'CO₂ Reduction (tons/year)',
          data: recommendations.map(rec => rec.carbonReduction || rec.annualEnergySavings * 0.000709),
          backgroundColor: 'rgba(16, 185, 129, 0.7)',
          borderColor: 'rgb(16, 185, 129)',
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          title: {
            display: true,
            text: 'CO₂ Reduction (tons/year)'
          },
          beginAtZero: true
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `CO₂ Reduction: ${formatNumber(context.raw as number, 1)} tons/year`;
            }
          }
        }
      }
    }
  });
}

function createSensitivityCurvesChart(ctx: CanvasRenderingContext2D, analyticsData: any, recommendations: any[] = [], translations: any = {}) {
  // If we have analyticsData, use it, otherwise create some plausible data
  const data = analyticsData?.sensitivityAnalysis || {
    utilityRate: { minus20: 25400, minus10: 33875, base: 42350, plus10: 50825, plus20: 59300 },
    operatingHours: { minus20: 21175, minus10: 31762, base: 42350, plus10: 52937, plus20: 63525 },
    maintenanceCost: { minus20: 38115, minus10: 40232, base: 42350, plus10: 44467, plus20: 46585 }
  };
  
  const baseNpv = recommendations[0]?.npv || 42350;
  
  return new Chart(ctx, {
    type: 'line',
    data: {
      labels: ['-20%', '-10%', 'Base', '+10%', '+20%'],
      datasets: [
        {
          label: 'Utility Rate',
          data: [
            data.utilityRate?.minus20 || baseNpv * 0.6,
            data.utilityRate?.minus10 || baseNpv * 0.8,
            data.utilityRate?.base || baseNpv,
            data.utilityRate?.plus10 || baseNpv * 1.2,
            data.utilityRate?.plus20 || baseNpv * 1.4
          ],
          borderColor: 'rgba(14, 165, 233, 1)',
          tension: 0.1
        },
        {
          label: 'Operating Hours',
          data: [
            data.operatingHours?.minus20 || baseNpv * 0.5,
            data.operatingHours?.minus10 || baseNpv * 0.75,
            data.operatingHours?.base || baseNpv,
            data.operatingHours?.plus10 || baseNpv * 1.25,
            data.operatingHours?.plus20 || baseNpv * 1.5
          ],
          borderColor: 'rgba(168, 85, 247, 1)',
          tension: 0.1
        },
        {
          label: 'Maintenance Cost',
          data: [
            data.maintenanceCost?.minus20 || baseNpv * 0.9,
            data.maintenanceCost?.minus10 || baseNpv * 0.95,
            data.maintenanceCost?.base || baseNpv,
            data.maintenanceCost?.plus10 || baseNpv * 1.05,
            data.maintenanceCost?.plus20 || baseNpv * 1.1
          ],
          borderColor: 'rgba(251, 146, 60, 1)',
          tension: 0.1
        }
      ]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: 'NPV ($)'
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${formatCurrency(context.raw as number)}`;
            }
          }
        }
      }
    }
  });
}

/**
 * Creates a waterfall chart showing the breakdown of costs and savings
 * for the recommended pump/motor replacement
 */
function createWaterfallChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  const initialCost = recommendation.estimatedPrice + 
    (recommendation.installationCost || recommendation.estimatedPrice * 0.25);
  
  const energySavings = recommendation.annualEnergySavings * analysis.utilityRate * 10; // 10-year energy savings
  const maintenanceSavings = (recommendation.maintenanceSavings || 250) * 10; // 10-year maintenance savings
  const otherSavings = recommendation.otherSavings || 500; // Other savings (one-time)
  const disposalCosts = recommendation.disposalCosts || -300; // Disposal costs (negative value)
  const totalValue = initialCost * -1 + energySavings + maintenanceSavings + otherSavings + disposalCosts;
  
  // Waterfall chart data
  const labels = ['Initial Cost', 'Energy Savings', 'Maintenance Savings', 'Other Savings', 'Disposal Costs', 'Net Value'];
  const values = [
    initialCost * -1,     // Initial cost is negative
    energySavings,        // Energy savings
    maintenanceSavings,   // Maintenance savings
    otherSavings,         // Other savings
    disposalCosts,        // Disposal costs
    0                     // Net value (calculated in code below)
  ];
  
  // Calculate running total for waterfall effect
  const runningTotal = [];
  let cumulative = 0;
  for (let i = 0; i < values.length - 1; i++) {
    runningTotal.push(null); // Start with null for the bar
    cumulative += values[i];
    runningTotal.push(cumulative); // End with the running total
  }
  runningTotal.push(null); // Final bar
  
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: labels,
      datasets: [
        // The visible bars
        {
          label: 'Value',
          data: [...values.slice(0, -1), totalValue], // Replace last 0 with total
          backgroundColor: [
            'rgba(244, 63, 94, 0.8)',   // Red for costs
            'rgba(16, 185, 129, 0.8)',  // Green for energy savings
            'rgba(16, 185, 129, 0.8)',  // Green for maintenance
            'rgba(16, 185, 129, 0.8)',  // Green for other savings
            'rgba(244, 63, 94, 0.8)',   // Red for disposal costs
            'rgba(59, 130, 246, 0.8)'   // Blue for net
          ],
          borderColor: [
            'rgb(244, 63, 94)',
            'rgb(16, 185, 129)',
            'rgb(16, 185, 129)',
            'rgb(16, 185, 129)',
            'rgb(244, 63, 94)',
            'rgb(59, 130, 246)'
          ],
          borderWidth: 1
        },
        // Invisible dataset for the waterfall connectors
        {
          label: 'Running Total',
          data: runningTotal,
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderColor: 'rgba(0, 0, 0, 0.3)',
          borderWidth: 2,
          type: 'line'
        }
      ]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Value ($)'
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const value = context.raw as number;
              if (context.datasetIndex === 0) {
                return `${value < 0 ? 'Cost' : 'Savings'}: ${formatCurrency(Math.abs(value))}`;
              }
              return '';
            }
          }
        }
      }
    }
  });
}

/**
 * Creates a tornado diagram for sensitivity analysis
 */
function createTornadoDiagramChart(ctx: CanvasRenderingContext2D, analyticsData: any, recommendation: any, translations: any) {
  // Get sensitivity data or use defaults
  const sensitivityData = analyticsData?.sensitivityAnalysis || {};
  const baseNpv = recommendation?.npv || analyticsData?.baseNpv || 42000;
  
  // Prepare data for the horizontal bar chart
  const variables = [];
  const negativeImpacts = [];
  const positiveImpacts = [];
  
  // Process each sensitivity variable
  Object.entries(sensitivityData).forEach(([key, value]: [string, any]) => {
    const minusImpact = (value.minus20 - baseNpv) / baseNpv * 100;
    const plusImpact = (value.plus20 - baseNpv) / baseNpv * 100;
    
    // Format the variable name
    let variableName = key
      .replace(/([A-Z])/g, ' $1') // Add spaces before capital letters
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
    
    variables.push(variableName);
    negativeImpacts.push(minusImpact);
    positiveImpacts.push(plusImpact);
  });
  
  // If no data is available, use default data
  if (variables.length === 0) {
    variables.push('Utility Rate', 'Operating Hours', 'Maintenance Cost');
    negativeImpacts.push(-40, -50, -10);
    positiveImpacts.push(40, 50, 10);
  }
  
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: variables,
      datasets: [
        {
          label: '-20%',
          data: negativeImpacts,
          backgroundColor: 'rgba(244, 63, 94, 0.7)',
          borderColor: 'rgb(244, 63, 94)',
          borderWidth: 1
        },
        {
          label: '+20%',
          data: positiveImpacts,
          backgroundColor: 'rgba(16, 185, 129, 0.7)',
          borderColor: 'rgb(16, 185, 129)',
          borderWidth: 1
        }
      ]
    },
    options: {
      indexAxis: 'y', // Horizontal bar chart
      responsive: true,
      scales: {
        x: {
          title: {
            display: true,
            text: 'Impact on NPV (%)'
          },
          stacked: false
        },
        y: {
          stacked: false
        }
      },
      plugins: {
        legend: {
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${formatNumber(context.raw as number, 1)}% impact`;
            }
          }
        }
      }
    }
  });
}

/**
 * Creates an enhanced interactive load profile chart with zooming and seasonal views
 */
function createLoadProfileChart(ctx: CanvasRenderingContext2D, analysis: PumpAnalysis, translations: any) {
  // Get load profile data from analysis or use defaults
  const loadProfile: any[] = Array.isArray(analysis.loadProfile) ? analysis.loadProfile : [];
  const t = translations?.t || ((key: string) => key);
  
  // If detailed load profile data exists, use it
  // Otherwise, process simplified seasonal profile or create a default
  let datasets = [];
  let labels = [];
  
  if (loadProfile && loadProfile.length > 0) {
    // Process actual load profile data
    // Group by season if applicable
    const dailyHours = Array.from({length: 24}, (_, i) => i);
    labels = dailyHours.map(hour => `${hour}:00`);
    
    // Create seasonal datasets if applicable
    if (analysis.seasonalOperation) {
      // Extract seasonal data if available
      const seasons = ['summer', 'winter', 'spring_fall'];
      const seasonColors: Record<string, any> = {
        summer: {
          border: 'rgba(239, 68, 68, 1)',
          bg: 'rgba(239, 68, 68, 0.1)'
        },
        winter: {
          border: 'rgba(59, 130, 246, 1)',
          bg: 'rgba(59, 130, 246, 0.1)'
        },
        spring_fall: {
          border: 'rgba(16, 185, 129, 1)',
          bg: 'rgba(16, 185, 129, 0.1)'
        }
      };
      
      seasons.forEach(season => {
        // Filter load profile for the current season or generate if missing
        const seasonData = loadProfile.filter((point: any) => point.season === season);
        
        if (seasonData.length > 0) {
          const seasonLoads = dailyHours.map(hour => {
            const hourData = seasonData.find((point: any) => point.hour === hour);
            return hourData ? hourData.load : null;
          });
          
          datasets.push({
            label: t(`loadProfile.${season}`) || season.replace('_', '/').charAt(0).toUpperCase() + season.replace('_', '/').slice(1),
            data: seasonLoads,
            borderColor: seasonColors[season].border,
            backgroundColor: seasonColors[season].bg,
            tension: 0.3,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 5
          });
        }
      });
      
      // If no seasonal data was found, fall back to a single dataset
      if (datasets.length === 0) {
        const loads = dailyHours.map(hour => {
          const hourData = loadProfile.find((point: any) => point.hour === hour);
          return hourData ? hourData.load : 0;
        });
        
        datasets.push({
          label: t('loadProfile.typical') || 'Typical Day',
          data: loads,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 5
        });
      }
    } else {
      // Process as a single dataset
      const loads = dailyHours.map(hour => {
        const hourData = loadProfile.find((point: any) => point.hour === hour);
        return hourData ? hourData.load : 0;
      });
      
      datasets.push({
        label: t('loadProfile.typical') || 'Typical Day',
        data: loads,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.3,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 5
      });
    }
  } else {
    // Create a default daily load profile based on pump schedule type
    const dailyHours = Array.from({length: 24}, (_, i) => i);
    labels = dailyHours.map(hour => `${hour}:00`);
    
    // Check if analysis uses seasonal operation data
    const seasonalOperation = (analysis as any).seasonalOperation;
    
    if (analysis.pumpSchedule === 'continuous') {
      // Continuous operation with some variation
      const loads = dailyHours.map(() => 80 + Math.floor(Math.random() * 20));
      
      datasets.push({
        label: t('loadProfile.continuous') || 'Continuous Operation',
        data: loads,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.3,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 5
      });
      
      // Add TOU rate overlay if enabled for this visualization
      if (analysis.tariffSchedule === 'tou') {
        const touRates = dailyHours.map(hour => {
          if (hour >= 12 && hour <= 18) {
            return 0.25; // Peak rate
          } else if ((hour >= 8 && hour <= 11) || (hour >= 19 && hour <= 21)) {
            return 0.15; // Mid-peak rate
          } else {
            return 0.08; // Off-peak rate
          }
        });
        
        datasets.push({
          label: t('loadProfile.touRates') || 'TOU Rates',
          data: touRates,
          borderColor: 'rgba(16, 185, 129, 1)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderDash: [5, 5],
          tension: 0,
          yAxisID: 'y1',
          pointRadius: 2,
          pointHoverRadius: 4
        });
      }
    } else if (analysis.pumpSchedule === 'intermittent') {
      // Intermittent operation - create multiple datasets for different day types
      const weekdayLoads = dailyHours.map(hour => {
        if (hour >= 8 && hour <= 18) {
          return 70 + Math.floor(Math.random() * 30); // Business hours
        } else if (hour >= 19 && hour <= 22) {
          return 40 + Math.floor(Math.random() * 20); // Evening hours
        } else {
          return 10 + Math.floor(Math.random() * 15); // Night hours
        }
      });
      
      const weekendLoads = dailyHours.map(hour => {
        if (hour >= 10 && hour <= 20) {
          return 50 + Math.floor(Math.random() * 30); // Weekend hours
        } else {
          return 5 + Math.floor(Math.random() * 10); // Low operation
        }
      });
      
      datasets.push(
        {
          label: t('loadProfile.weekday') || 'Weekday',
          data: weekdayLoads,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5
        },
        {
          label: t('loadProfile.weekend') || 'Weekend',
          data: weekendLoads,
          borderColor: 'rgba(239, 68, 68, 1)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.3,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      );
    } else if (seasonalOperation) {
      // Enhanced seasonal view showing all seasons when seasonal flag is enabled
      const summerLoads = dailyHours.map(hour => {
        if (hour >= 11 && hour <= 19) {
          return 85 + Math.floor(Math.random() * 15); // Peak summer
        } else if (hour >= 7 && hour <= 22) {
          return 60 + Math.floor(Math.random() * 20); // Day summer
        } else {
          return 30 + Math.floor(Math.random() * 15); // Night summer
        }
      });
      
      const winterLoads = dailyHours.map(hour => {
        if (hour >= 6 && hour <= 9 || hour >= 17 && hour <= 21) {
          return 80 + Math.floor(Math.random() * 20); // Peak winter
        } else if (hour >= 10 && hour <= 16) {
          return 50 + Math.floor(Math.random() * 15); // Day winter
        } else {
          return 20 + Math.floor(Math.random() * 10); // Night winter
        }
      });
      
      datasets.push(
        {
          label: t('loadProfile.summer') || 'Summer',
          data: summerLoads,
          borderColor: 'rgba(239, 68, 68, 1)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.3,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5
        },
        {
          label: t('loadProfile.winter') || 'Winter',
          data: winterLoads,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      );
      
      // Add spring/fall data if simplified values are available
      if (analysis.loadProfileSimplifiedSpringFall) {
        const springFallLoads = dailyHours.map(hour => {
          if (hour >= 9 && hour <= 18) {
            return 60 + Math.floor(Math.random() * 15); // Day spring/fall
          } else if (hour >= 6 && hour <= 8 || hour >= 19 && hour <= 21) {
            return 45 + Math.floor(Math.random() * 15); // Morning/evening spring/fall
          } else {
            return 15 + Math.floor(Math.random() * 10); // Night spring/fall
          }
        });
        
        datasets.push({
          label: t('loadProfile.springFall') || 'Spring/Fall',
          data: springFallLoads,
          borderColor: 'rgba(16, 185, 129, 1)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.3,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5
        });
      }
    } else {
      // Standard variable load profile when seasonal operation is not enabled
      const variableLoads = dailyHours.map(hour => {
        if (hour >= 8 && hour <= 18) {
          return 50 + Math.floor(Math.random() * 50); // Widely variable
        } else {
          return 10 + Math.floor(Math.random() * 20); // Low variable
        }
      });
      
      datasets.push({
        label: t('loadProfile.variable') || 'Variable Load',
        data: variableLoads,
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.3,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 5
      });
    }
  }
  
  // Add energy cost overlay if utility rates are available
  if (analysis.utilityRate) {
    // Create a mock TOU rate schedule if detailed rates not available
    const touRates = [0.13, 0.12, 0.11, 0.11, 0.12, 0.14, 0.16, 0.18, 0.20, 0.22, 
                      0.22, 0.20, 0.22, 0.24, 0.26, 0.28, 0.30, 0.32, 0.28, 0.24, 
                      0.20, 0.18, 0.16, 0.14];
                      
    datasets.push({
      label: t('loadProfile.energyCost') || 'Energy Cost',
      data: touRates,
      borderColor: 'rgba(139, 92, 246, 1)',
      backgroundColor: 'rgba(0, 0, 0, 0)',
      borderWidth: 2,
      borderDash: [5, 5],
      tension: 0,
      pointRadius: 0,
      pointHoverRadius: 0,
      yAxisID: 'y1',
    });
  }
  
  return new Chart(ctx, {
    type: 'line',
    data: {
      labels: labels,
      datasets: datasets
    },
    options: {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: t('loadProfile.load') || 'Load (%)'
          },
          min: 0,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        },
        y1: {
          type: 'linear',
          display: datasets.some(d => d.yAxisID === 'y1'),
          position: 'right',
          title: {
            display: true,
            text: t('loadProfile.rate') || 'Rate ($/kWh)'
          },
          min: 0,
          max: datasets.some(d => (d as any).yAxisID === 'y1') ? Math.max(...(datasets.find(d => (d as any).yAxisID === 'y1') as any).data) * 1.2 : 0.5,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: function(value) {
              return '$' + formatNumber(value as number, 2);
            }
          }
        },
        x: {
          title: {
            display: true,
            text: t('loadProfile.hourOfDay') || 'Hour of Day'
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            title: function(tooltipItems) {
              const item = tooltipItems[0];
              const hour = parseInt(item.label);
              let timeContext = '';
              
              // Add time context information
              if (hour >= 7 && hour <= 9) {
                timeContext = t('loadProfile.morningPeak') || 'Morning Peak';
              } else if (hour >= 10 && hour <= 16) {
                timeContext = t('loadProfile.businessHours') || 'Business Hours';
              } else if (hour >= 17 && hour <= 20) {
                timeContext = t('loadProfile.eveningPeak') || 'Evening Peak';
              } else {
                timeContext = t('loadProfile.offHours') || 'Off Hours';
              }
              
              return `${item.label} - ${timeContext}`;
            },
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              
              if (context.dataset.yAxisID === 'y1') {
                let tooltipText = `${label}: $${formatNumber(value, 2)}/kWh`;
                
                // Add cost implication if load data is available
                const loadDatasets = context.chart.data.datasets.filter(d => (d as any).yAxisID !== 'y1');
                if (loadDatasets.length > 0) {
                  // Use first load dataset if multiple exist
                  const loadAtThisTime = loadDatasets[0].data[context.dataIndex];
                  if (typeof loadAtThisTime === 'number') {
                    const costImplication = (loadAtThisTime / 100) * value;
                    tooltipText += ` (Est. $${formatNumber(costImplication, 2)}/kWh at current load)`;
                  }
                }
                return tooltipText;
              } else {
                let tooltipText = `${label}: ${formatNumber(value, 1)}% load`;
                
                // If TOU rates are shown, add rate information
                const rateDatasets = context.chart.data.datasets.filter(d => (d as any).yAxisID === 'y1');
                if (rateDatasets.length > 0) {
                  const rateAtThisTime = rateDatasets[0].data[context.dataIndex];
                  if (typeof rateAtThisTime === 'number') {
                    tooltipText += ` (Rate: $${formatNumber(rateAtThisTime, 2)}/kWh)`;
                  }
                }
                return tooltipText;
              }
            }
          }
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'xy'
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true
            },
            mode: 'xy',
          }
        }
      }
    }
  });
}

/**
 * Creates a lifecycle cost chart comparing existing and new pump over time
 */
function createLifecycleCostChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  // Time period for lifecycle analysis
  const years = Array.from({length: 15}, (_, i) => i);
  
  // Current system costs
  const currentAnnualEnergy = recommendation.annualEnergySavings * 2 * analysis.utilityRate;
  const currentAnnualMaintenance = analysis.maintenanceCost || 2000;
  const currentAnnualTotal = currentAnnualEnergy + currentAnnualMaintenance;
  
  // New system costs
  const newSystemInitial = recommendation.estimatedPrice + 
    (recommendation.installationCost || recommendation.estimatedPrice * 0.25);
  const newAnnualEnergy = recommendation.annualEnergySavings * analysis.utilityRate;
  const newAnnualMaintenance = (analysis.maintenanceCost || 2000) * 0.5;
  const newAnnualTotal = newAnnualEnergy + newAnnualMaintenance;
  
  // Calculate lifecycle costs
  const currentLifecycle = years.map(year => currentAnnualTotal * year);
  const newLifecycle = years.map(year => newSystemInitial + (newAnnualTotal * year));
  
  // Savings over time
  const savings = years.map((_, index) => 
    currentLifecycle[index] - newLifecycle[index]
  );
  
  return new Chart(ctx, {
    type: 'line',
    data: {
      labels: years.map(year => `Year ${year}`),
      datasets: [
        {
          label: 'Current System',
          data: currentLifecycle,
          borderColor: 'rgba(244, 63, 94, 1)',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderWidth: 2
        },
        {
          label: 'New System',
          data: newLifecycle,
          borderColor: 'rgba(16, 185, 129, 1)',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderWidth: 2
        },
        {
          label: 'Cumulative Savings',
          data: savings,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          fill: true
        }
      ]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Cost ($)'
          },
          beginAtZero: true
        },
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              if (context.dataset.label === 'Cumulative Savings') {
                return `${context.dataset.label}: ${formatCurrency(context.raw as number)}`;
              }
              return `${context.dataset.label}: ${formatCurrency(context.raw as number)}`;
            }
          }
        }
      }
    }
  });
}

/**
 * Creates an enhanced efficiency map chart showing efficiency across different operating conditions
 * with interactive features and better visualization
 */
function createEfficiencyMapChart(ctx: CanvasRenderingContext2D, recommendation: any, analysis: PumpAnalysis, translations: any) {
  const t = translations?.t || ((key: string) => key);
  
  // Generate enhanced efficiency map data
  const data = [];
  const columns = 20; // Increased resolution
  const rows = 20;
  
  // Design point from recommendation or analysis
  const designFlow = recommendation.specifications?.performance?.flow_gpm || analysis.flow || 1000;
  const designHead = recommendation.specifications?.performance?.head_ft || analysis.head || 100;
  
  // Base efficiency from recommendation
  const baseEfficiency = recommendation.specifications?.motor?.efficiency_percentage || recommendation.pumpEfficiency || 85;
  
  // Define operational boundaries
  const minFlow = 0.4; // 40% of design flow
  const maxFlow = 1.6; // 160% of design flow
  const minHead = 0.4; // 40% of design head
  const maxHead = 1.6; // 160% of design head
  
  // Create a grid of data points for the efficiency map
  // X-axis: Flow rate (actual values)
  // Y-axis: Head (actual values)
  // Z-axis (color): Efficiency %
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < columns; j++) {
      // Calculate normalized positions (0-1)
      const x = j / (columns - 1);
      const y = i / (rows - 1);
      
      // Scale to actual flow and head values
      const flowValue = designFlow * (minFlow + (x * (maxFlow - minFlow)));
      const headValue = designHead * (minHead + (y * (maxHead - minHead)));
      
      // Flow percentage and head percentage for calculations
      const flowPercent = 100 * (flowValue / designFlow);
      const headPercent = 100 * (headValue / designHead);
      
      // Efficiency calculations - improved formula based on pump affinity laws
      // Uses elliptical distance from BEP with different weights for flow and head
      const flowDist = Math.pow((flowPercent - 100) / 60, 2);
      const headDist = Math.pow((headPercent - 100) / 40, 2); // Head affects efficiency more severely
      const distance = Math.sqrt(flowDist + headDist);
      
      // Apply more realistic efficiency curve
      // Sharp drop-off at extremes, more gradual near BEP
      let efficiency: number;
      
      if (distance < 0.5) {
        // Near BEP: small, gentle efficiency reduction
        efficiency = baseEfficiency - (distance * distance * 10);
      } else if (distance < 1.0) {
        // Medium distance: linear reduction
        efficiency = baseEfficiency - 2.5 - (distance * 20);
      } else {
        // Far from BEP: steep reduction
        efficiency = baseEfficiency - 22.5 - (distance * 30);
      }
      
      // Add special considerations for specific conditions
      // Low flow conditions cause additional efficiency loss (cavitation risk)
      if (flowPercent < 60) {
        efficiency -= (60 - flowPercent) * 0.5;
      }
      
      // Very high head conditions also reduce efficiency (pressure effects)
      if (headPercent > 130) {
        efficiency -= (headPercent - 130) * 0.3;
      }
      
      // Ensure reasonable bounds
      efficiency = Math.max(10, Math.min(baseEfficiency, Math.floor(efficiency)));
      
      // Add operational point markers
      let pointType = 'normal';
      
      // Mark the design point (BEP)
      if (Math.abs(flowPercent - 100) < 5 && Math.abs(headPercent - 100) < 5) {
        pointType = 'bep';
      }
      
      // Mark system curve intersection (if available in recommendation)
      const sysFlow = recommendation.systemOperatingPoint?.flow_gpm;
      const sysHead = recommendation.systemOperatingPoint?.head_ft;
      
      if (sysFlow && sysHead) {
        const sysFlowPercent = 100 * (sysFlow / designFlow);
        const sysHeadPercent = 100 * (sysHead / designHead);
        
        if (Math.abs(flowPercent - sysFlowPercent) < 5 && Math.abs(headPercent - sysHeadPercent) < 5) {
          pointType = 'system';
        }
      }
      
      // Add current operating point from analysis
      const operatingPoint = (analysis as any).operatingPoint;
      if (operatingPoint?.flow && operatingPoint?.head) {
        const opFlowPercent = 100 * (operatingPoint.flow / designFlow);
        const opHeadPercent = 100 * (operatingPoint.head / designHead);
        
        if (Math.abs(flowPercent - opFlowPercent) < 5 && Math.abs(headPercent - opHeadPercent) < 5) {
          pointType = 'current';
        }
      }
      
      data.push({
        x: flowValue,
        y: headValue,
        v: efficiency,
        flowPct: flowPercent,
        headPct: headPercent,
        pointType: pointType
      });
    }
  }
  
  // Create contour lines data
  // Group points by efficiency bands (every 5%)
  const efficiencyBands: Record<number, {x: number, y: number}[]> = {};
  const bandValues = [95, 90, 85, 80, 75, 70, 65, 60, 50, 40];
  
  bandValues.forEach(band => {
    efficiencyBands[band] = [];
  });
  
  // Identify points near each efficiency band
  data.forEach((point: any) => {
    bandValues.forEach(band => {
      if (Math.abs(point.v - band) <= 2.5) {
        efficiencyBands[band].push({
          x: point.x,
          y: point.y
        });
      }
    });
  });
  
  // Create contour datasets
  const contourDatasets = bandValues.map(band => ({
    label: `${band}% Efficiency`,
    data: efficiencyBands[band],
    showLine: true,
    fill: false,
    pointRadius: 0,
    borderColor: 
      band >= 90 ? 'rgba(16, 185, 129, 0.6)' : // Green
      band >= 80 ? 'rgba(59, 130, 246, 0.6)' : // Blue
      band >= 70 ? 'rgba(249, 115, 22, 0.6)' : // Orange
               'rgba(244, 63, 94, 0.6)',       // Red
    borderWidth: 2,
    borderDash: [3, 3],
    tension: 0.4
  }));
  
  // Special markers for key points
  const markers = [];
  
  // BEP marker
  markers.push({
    label: t('efficiency.bep') || 'Best Efficiency Point',
    data: [{
      x: designFlow,
      y: designHead
    }],
    pointBackgroundColor: 'rgba(16, 185, 129, 1)',
    pointBorderColor: 'white',
    pointBorderWidth: 2,
    pointRadius: 10,
    pointStyle: 'star'
  });
  
  // System operating point marker (if available)
  if (recommendation.systemOperatingPoint?.flow_gpm && recommendation.systemOperatingPoint?.head_ft) {
    markers.push({
      label: t('efficiency.systemPoint') || 'System Operating Point',
      data: [{
        x: recommendation.systemOperatingPoint.flow_gpm,
        y: recommendation.systemOperatingPoint.head_ft
      }],
      pointBackgroundColor: 'rgba(249, 115, 22, 1)',
      pointBorderColor: 'white',
      pointBorderWidth: 2,
      pointRadius: 10,
      pointStyle: 'triangle'
    });
  }
  
  // Current operating point marker
  const operatingPoint = (analysis as any).operatingPoint;
  if (operatingPoint?.flow && operatingPoint?.head) {
    markers.push({
      label: t('efficiency.currentPoint') || 'Current Operating Point',
      data: [{
        x: operatingPoint.flow,
        y: operatingPoint.head
      }],
      pointBackgroundColor: 'rgba(244, 63, 94, 1)',
      pointBorderColor: 'white',
      pointBorderWidth: 2,
      pointRadius: 10,
      pointStyle: 'circle'
    });
  }
  
  // Create a custom chart with a heatmap-like appearance plus contour lines
  return new Chart(ctx, {
    type: 'scatter',
    data: {
      datasets: [
        // Efficiency points (background)
        {
          label: t('efficiency.efficiency') || 'Efficiency (%)',
          data: data,
          backgroundColor: function(context) {
            const value = (context.raw as any).v;
            // Enhanced color scale from red (low efficiency) to green (high efficiency)
            if (value > 90) return 'rgba(16, 185, 129, 0.8)'; // Green for excellent
            if (value > 85) return 'rgba(16, 185, 129, 0.6)'; // Green for very good
            if (value > 80) return 'rgba(59, 130, 246, 0.8)'; // Blue for good
            if (value > 75) return 'rgba(59, 130, 246, 0.6)'; // Blue for above average
            if (value > 70) return 'rgba(249, 115, 22, 0.8)'; // Orange for average
            if (value > 65) return 'rgba(249, 115, 22, 0.6)'; // Orange for below average
            if (value > 60) return 'rgba(244, 63, 94, 0.6)'; // Red for poor
            return 'rgba(244, 63, 94, 0.8)'; // Red for very poor
          },
          borderColor: 'rgba(255, 255, 255, 0.3)',
          borderWidth: 0.5,
          pointRadius: 6,
          pointHoverRadius: 8
        },
        // Add contour lines
        ...contourDatasets,
        // Add markers for key points
        ...markers
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          type: 'linear',
          title: {
            display: true,
            text: t('efficiency.head') || 'Head (ft)'
          },
          min: designHead * minHead * 0.9,
          max: designHead * maxHead * 1.1
        },
        x: {
          type: 'linear',
          title: {
            display: true,
            text: t('efficiency.flow') || 'Flow (GPM)'
          },
          min: designFlow * minFlow * 0.9,
          max: designFlow * maxFlow * 1.1
        }
      },
      interaction: {
        mode: 'nearest',
        intersect: true
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            filter: (legendItem) => {
              // Only show BEP, system point, current point, and a few efficiency lines
              return legendItem.text.includes('Point') || 
                     legendItem.text.includes('90%') || 
                     legendItem.text.includes('80%') || 
                     legendItem.text.includes('70%');
            }
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              if (context.datasetIndex === 0) {
                const raw = context.raw as any;
                return [
                  `Flow: ${formatNumber(raw.x, 0)} GPM (${formatNumber(raw.flowPct, 0)}%)`,
                  `Head: ${formatNumber(raw.y, 0)} ft (${formatNumber(raw.headPct, 0)}%)`,
                  `Efficiency: ${raw.v}%`
                ];
              } else {
                return context.dataset.label;
              }
            }
          }
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'xy'
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true
            },
            mode: 'xy'
          }
        }
      }
    }
  });
}
